// Carte des sprites dans la feuille (grille de cases 16x16).
//
// ⚠️ SOURCE DE VÉRITÉ : la table LAYOUT de scripts/gen-sprites.mjs. Ce fichier la
// recopie À LA MAIN (même colonnes/lignes). Si tu déplaces un sprite dans le script,
// mets à jour ici. Le test tests/sprites.test.ts vérifie la cohérence des dimensions
// (SHEET_W/SHEET_H) avec le PNG réellement encodé.
import { T, type MonkKind } from '../shared/types'
import type { WorldKey } from '../shared/worlds'
import { BIG_DECO_ORDER, BIG_DECOS, BIG_FRAMES, DECOS, TRESOR_CODE_MIN, type BigDecoKind, type DecoKind } from '../shared/decor'

export const SHEET_COLS = 16
export const CELL = 16
export const SHEET_W = 256 // = SHEET_COLS * CELL
export const SHEET_H = 352 // = 22 lignes * CELL (+ mecanismes, + smears, + saut trampoline, + LOOTS Mine l.19, + COMPAGNONS premium l.20-21 (21/07))

export interface SpriteRef { readonly sx: number; readonly sy: number } // pixels dans la feuille
const ref = (col: number, row: number): SpriteRef => ({ sx: col * CELL, sy: row * CELL })

// --- tuiles de terrain : valeur de l'enum T -> sprite (FalseBrick réutilise Brick) ---
const brick = ref(0, 0)
export const TILE_SPRITES: Record<number, SpriteRef> = {
  [T.Brick]: brick,
  [T.Concrete]: ref(1, 0),
  [T.Ladder]: ref(2, 0),
  [T.Rope]: ref(3, 0),
  [T.FalseBrick]: brick, // rendu identique à la brique (le distinguo est côté éditeur)
  [T.Ice]: ref(4, 0),
  [T.Sand]: ref(5, 0),
  [T.Teleporter]: ref(6, 0),
  [T.ExitLadder]: ref(7, 0),
  // Tuiles danger Phase 1 (ligne 2 du LAYOUT gen-sprites). Comportements = Tasks 2/3.
  [T.Crumble]: ref(0, 2),
  [T.Lava]: ref(1, 2),
  // Tuiles mouvement Phase 2 (ligne 2, cols 4/5/6). Solides ; comportements = Tasks 2/3.
  [T.ConveyorL]: ref(4, 2),
  [T.ConveyorR]: ref(5, 2),
  [T.Trampoline]: ref(6, 2),
  // Tuile Phase 3 (ligne 2, col 7) : porte fermée. Solide ; ouverture à la clé = Tasks 2/3.
  [T.DoorClosed]: ref(7, 2),
  // Tuiles Lot C (ligne 2, cols 9-13). ConveyorUp/Down = tapis vertical (NON solide, on y
  // entre) ; BombRefill = station de bombes rouges (NON solide) ; TrampolineL/R = trampolines
  // directionnels (SOLIDES). Solidité définie côté shared/physics.ts, pas ici (rendu seul).
  [T.ConveyorUp]: ref(9, 2),
  [T.ConveyorDown]: ref(10, 2),
  [T.BombRefill]: ref(11, 2),
  [T.TrampolineL]: ref(12, 2),
  [T.TrampolineR]: ref(13, 2),
  [T.DeathZone]: ref(14, 2), // #1 : champ mortel traversable (non solide, cf. shared/physics.ts)
  [T.TrampolineStrong]: ref(15, 2), // trampoline puissant (16/07) : rebond 5 cases, toile rouge
  [T.Water]: ref(12, 9), // eau (16/07) : non solide, nage + souffle (ligne 9 après mondes/caisses)
  [T.Fire]: ref(13, 9),   // feu (16/07) : non solide, mortel au contact (flammes animées au rendu)
  [T.Spikes]: ref(14, 9), // pics (16/07) : non solides, mortels en chute uniquement
  [T.RampUpRight]: ref(10, 14), // rampe '/' (16/07) : solide, montee diagonale vers la droite
  [T.RampUpLeft]: ref(11, 14),  // rampe '\\' (16/07) : solide, montee diagonale vers la gauche
  [T.Bubbles]: ref(15, 9), // source de bulles (16/07 soir) : recharge le souffle. [15,9] : [13,9] etait le FEU (collision 2 sprites/1 case)
  [T.TrampolineStrongL]: ref(14, 13), // directionnel PUISSANT haut-gauche (16/07 soir, C1) : rampe penchée rouge
  [T.TrampolineStrongR]: ref(15, 13), // directionnel PUISSANT haut-droite
  [T.Button]: ref(0, 16), // interrupteur (17/07) : non solide, presse par occupation (coureur/moine/caisse)
  // (variantes désert : voir DESERT_* plus bas — versions chaudes aiguillées par biome au rendu)
  [T.Barrier]: ref(1, 16), // barriere FERMEE (solide) ; le circuit la mute en BarrierOpen
  [T.BarrierOpen]: ref(2, 16), // barriere OUVERTE (traversable) — etat runtime, jamais dans l'ASCII
  [T.OneWayR]: ref(3, 16), // sens unique gauche->droite (17/07)
  [T.OneWayL]: ref(4, 16), // sens unique droite->gauche
  [T.DashCrystal]: ref(5, 16), // cristal de dash (17/07) : station de recharge du cooldown
}

// Téléporteurs teintés : 3 variantes de teinte (une par paire, cyclée i%3) pour montrer
// visuellement quelle entrée relie quelle sortie. Variante 0 = le sprite violet historique
// [6,0] (= TILE_SPRITES[T.Teleporter]) ; 1 = cyan [2,2] ; 2 = ambre [3,2]. Synchro LAYOUT.
export const TELEPORTERS: readonly SpriteRef[] = [ref(6, 0), ref(2, 2), ref(3, 2)]

// --- tuiles structurelles PAR MONDE (thèmes) : brique + béton recolorés. Le reste des tuiles
// (échelle, corde, glace, sable, téléporteur, sortie) est COMMUN via TILE_SPRITES. classique
// réutilise les positions de la ligne 0 ; les 5 autres mondes occupent la ligne 9 par paires
// (brique, béton) côte à côte (cf. gen-sprites LAYOUT, mêmes colonnes). ---
// + crumble/rampes PAR MONDE (16/07, retour Claude-Alain) : ligne 12 (classique = cases historiques).
// hole0/hole1 (audit 16/07, ligne 15) : trou creusé OUVERT / repousse imminente, aux couleurs de la
// brique du monde — creuser dans la neige n'ouvre plus une brique rouge. `classique` garde ses cases
// historiques (sprites byte-identiques).
export const WORLD_TILES: Record<WorldKey, { brick: SpriteRef; concrete: SpriteRef; crumble: SpriteRef; rampR: SpriteRef; rampL: SpriteRef; hole0: SpriteRef; hole1: SpriteRef }> = {
  classique: { brick: ref(0, 0), concrete: ref(1, 0), crumble: ref(0, 2), rampR: ref(10, 14), rampL: ref(11, 14), hole0: ref(12, 0), hole1: ref(13, 0) },
  grotte: { brick: ref(0, 9), concrete: ref(1, 9), crumble: ref(0, 12), rampR: ref(1, 12), rampL: ref(2, 12), hole0: ref(0, 14), hole1: ref(1, 14) },
  prairie: { brick: ref(2, 9), concrete: ref(3, 9), crumble: ref(3, 12), rampR: ref(4, 12), rampL: ref(5, 12), hole0: ref(2, 14), hole1: ref(3, 14) },
  desert: { brick: ref(4, 9), concrete: ref(5, 9), crumble: ref(6, 12), rampR: ref(7, 12), rampL: ref(8, 12), hole0: ref(4, 14), hole1: ref(5, 14) },
  neige: { brick: ref(6, 9), concrete: ref(7, 9), crumble: ref(9, 12), rampR: ref(10, 12), rampL: ref(11, 12), hole0: ref(6, 14), hole1: ref(7, 14) },
  usine: { brick: ref(8, 9), concrete: ref(9, 9), crumble: ref(12, 12), rampR: ref(13, 12), rampL: ref(14, 12), hole0: ref(8, 14), hole1: ref(9, 14) },
  // Mondes 7 et 8 (16/07 nuit) : ligne 9 étant pleine, tout leur jeu de tuiles tient sur la ligne 16.
  abysses: { brick: ref(0, 15), concrete: ref(1, 15), crumble: ref(2, 15), rampR: ref(3, 15), rampL: ref(4, 15), hole0: ref(5, 15), hole1: ref(6, 15) },
  manoir: { brick: ref(7, 15), concrete: ref(8, 15), crumble: ref(9, 15), rampR: ref(10, 15), rampL: ref(11, 15), hole0: ref(12, 15), hole1: ref(13, 15) },
}

// --- VER CHECKPOINT (16/07 soir, ligne 13) : 5 segments queue -> tête (tête à DROITE), l'ancre
// '_' est la case CENTRALE (index 2). Dormant = teinte terne + yeux fermés ; éveillé = le ver du
// checkpoint ACTIF du coureur caméra (rose vif + gros yeux). Dessiné en overlay (drawWorms). ---
export const WORM_SLEEP: readonly SpriteRef[] = [ref(0, 13), ref(1, 13), ref(2, 13), ref(3, 13), ref(4, 13)]
export const WORM_WAKE: readonly SpriteRef[] = [ref(5, 13), ref(6, 13), ref(7, 13), ref(8, 13), ref(9, 13)]

// --- ENNEMIS AQUATIQUES (16/07 soir, fin de ligne 14) : 2 frames chacun. Le piranha regarde
// à GAUCHE dans la feuille (le rendu flip selon face) ; la méduse pulse (cloche large/contractée). ---
export const FISH_FRAMES: Record<'piranha' | 'meduse', readonly [SpriteRef, SpriteRef]> = {
  piranha: [ref(12, 14), ref(13, 14)],
  meduse: [ref(14, 14), ref(15, 14)],
}

// --- décorations (couche cosmétique plaçable) : position dérivée du CODE — col (code-1)%16,
// ligne 10 + floor((code-1)/16) (codes 1-16 = ligne 10, 17-32 = ligne 11). Dérivé de DECOS
// (shared/decor.ts), pas de recopie à la main (LAYOUT de gen-sprites suit la même formule). ---
// Deux plages de cellules : codes 1-31 -> lignes 10-11 historiques ; codes 32+ (TRÉSORS, 21/07) ->
// lignes 44-45 AJOUTÉES sous les biomes HD de world-tiles (les lignes 12+ de la feuille 16 px sont
// prises par tuiles/moines). La 2e plage est HD-only : sans feuille monde chargée, le fallback 16 px
// tombe hors de spritesheet.png et ne dessine rien (dégradation acceptée, la feuille charge toujours).
const decoRef = (code: number): SpriteRef => code >= TRESOR_CODE_MIN
  ? ref((code - TRESOR_CODE_MIN) % 16, 44 + Math.floor((code - TRESOR_CODE_MIN) / 16))
  : ref((code - 1) % 16, 10 + Math.floor((code - 1) / 16))

// VARIANTES DÉSERT (22/07, audit « Phare à on! » validé par Claude-Alain) : versions CHAUDES des
// éléments froids, servies au rendu quand world === 'desert'. Cellules re-teintées par
// gen-sprites (copyCellWarm : teinte imposée, luminance conservée) sur la ligne 16.
export const DESERT_SPIKES: SpriteRef = ref(11, 16)       // pics -> os clair
export const DESERT_CRATE_METAL: SpriteRef = ref(12, 16)  // caisse métal -> laiton
export const DESERT_DECO_BY_CODE: Record<number, SpriteRef> = {
  [DECOS.chaine.code]: ref(13, 16),      // chaîne -> bronze
  [DECOS.stalagmite.code]: ref(14, 16),  // stalagmite -> grès
  [DECOS.cristal.code]: ref(15, 16),     // cristal -> ambre
}

export const DECO_SPRITES: Record<DecoKind, SpriteRef> = Object.fromEntries(
  (Object.keys(DECOS) as DecoKind[]).map((k) => [k, decoRef(DECOS[k].code)]),
) as Record<DecoKind, SpriteRef>

// --- objets ---
export const GOLD: SpriteRef = ref(8, 0)
export const BOMB_ITEM: SpriteRef = ref(9, 0)
export const JACK_ITEM: SpriteRef = ref(10, 0)
export const BOMB_LIT: SpriteRef = ref(11, 0)
export const HOLE_STAGES: readonly [SpriteRef, SpriteRef] = [ref(12, 0), ref(13, 0)] // ouvert, repousse imminente
export const TAZER_ITEM: SpriteRef = ref(14, 0)
export const KEY_ITEM: SpriteRef = ref(8, 2) // clé au sol (Phase 3, ligne 2 col 8)
export const ROPE_ITEM: SpriteRef = ref(15, 0) // objet corde (#4, ligne 0 col 15 libre)
export const CRATE_WOOD: SpriteRef = ref(10, 9)  // caisse bois (Phase 4, #2 ; ligne 9 col 10 libre)
export const CRATE_METAL: SpriteRef = ref(11, 9) // caisse métal (Phase 4, #2 ; ligne 9 col 11 libre)
export const CRATE_BOULDER: readonly [SpriteRef, SpriteRef] = [ref(14, 15), ref(15, 15)] // ROCHER (17/07) : 2 frames (rotation au roulement)

// --- LOOTS DE LA MINE (Lot C, ligne 19 : feuille agrandie 304->320) : cosmétiques rendus HORS
// simulation. Cellules VIERGES ajoutées (aucune ancienne cellule coureur — désormais sur
// runners.png — n'est réutilisée). Synchro LAYOUT de gen-sprites.mjs (petChat0..briqueMystere). ---
export const PET_CHAT: readonly [SpriteRef, SpriteRef] = [ref(0, 19), ref(1, 19)]        // chat mignon (trot, 2 frames)
export const PET_CANARI: readonly [SpriteRef, SpriteRef] = [ref(2, 19), ref(3, 19)]      // canari doré (vol, ailes battantes)
// COMPAGNONS premium (21/07, ligne 20) : 8 pets x 2 frames — maquettes /pages/loots-premium.html.
export const PET_SPRITES: Readonly<Record<string, readonly [SpriteRef, SpriteRef]>> = {
  'pet-chat': [ref(0, 19), ref(1, 19)], 'pet-canari': [ref(2, 19), ref(3, 19)],
  'pet-perroquet': [ref(0, 20), ref(1, 20)], 'pet-araignee': [ref(2, 20), ref(3, 20)],
  'pet-serpent': [ref(4, 20), ref(5, 20)], 'pet-loup': [ref(6, 20), ref(7, 20)],
  'pet-chouette': [ref(8, 20), ref(9, 20)], 'pet-renard': [ref(10, 20), ref(11, 20)],
  'pet-dragonnet': [ref(12, 20), ref(13, 20)], 'pet-follet': [ref(14, 20), ref(15, 20)],
}
export const PIOCHE_MARTEAU: readonly [SpriteRef, SpriteRef] = [ref(4, 19), ref(5, 19)]  // marteau doré : [icône, frame d'impact]
export const PIOCHE_FOREUSE: readonly [SpriteRef, SpriteRef] = [ref(6, 19), ref(7, 19)]  // foreuse (vibration, 2 frames)
export const DECO_STATUE: SpriteRef = ref(8, 19)                                          // statue dorée (déco d'éditeur)
export const BRIQUE_MYSTERE: SpriteRef = ref(9, 19)                                       // brique mystère des socles (point d'interrogation doré)

// --- coureur (J1, ligne 1). J2 = mêmes frames décalées de RUNNER2_OFFSET_ROWS lignes ---
export interface RunnerFrames {
  readonly run: readonly [SpriteRef, SpriteRef]
  readonly climb: readonly [SpriteRef, SpriteRef]
  readonly rope: readonly [SpriteRef, SpriteRef]
  readonly fall: SpriteRef
  readonly dig: SpriteRef
  readonly idle: SpriteRef
  // DASH CRISTAL INCARNÉ (18/07) : poses dédiées — accroupi de charge, fusée à 2 temps, freinage.
  readonly crouch: SpriteRef
  readonly dash: readonly [SpriteRef, SpriteRef]
  readonly brake: SpriteRef
  // COUP DE PIOCHE (18/07, maquette B creusage) : l'impact (plantée) puis la pioche se relève.
  readonly pickUp: SpriteRef
  readonly pickHit: SpriteRef
}
export const RUNNER_FRAMES: RunnerFrames = {
  idle: ref(0, 1),
  run: [ref(1, 1), ref(2, 1)],
  climb: [ref(3, 1), ref(4, 1)],
  rope: [ref(5, 1), ref(6, 1)],
  fall: ref(7, 1),
  dig: ref(8, 1),
  crouch: ref(9, 1),
  dash: [ref(10, 1), ref(11, 1)],
  brake: ref(12, 1),
  pickUp: ref(13, 1),
  pickHit: ref(14, 1),
}
// Éclatement du creusage (18/07) : 3 calques TRANSPARENTS superposés au trou pendant ses
// ~8 premiers ticks (fissures -> le centre cède -> miettes). Posés sur la tuile du biome.
export const DIG_CRACKS: readonly SpriteRef[] = [ref(8, 16), ref(9, 16), ref(10, 16)]
// Smears du dash (18/07, variantes aléatoires) : frame étirée 32 px (2 cases), 2 frames par
// joueur (ligne 17 : J1 cols 0/2, J2 cols 4/6, J3 cols 8/10 — la moitié droite suit).
export const SMEAR_FRAMES: readonly (readonly [SpriteRef, SpriteRef])[] = [
  [ref(0, 17), ref(2, 17)],
  [ref(4, 17), ref(6, 17)],
  [ref(8, 17), ref(10, 17)],
]
// Cristallisation du burst : 2 frames PARTAGÉES J1/J2/J3 (ligne 16 — pas de variante couleur :
// pendant le burst, la glace remplace la couleur du joueur, qui revient au freinage).
export const RUNNER_DASH_ICE: readonly [SpriteRef, SpriteRef] = [ref(6, 16), ref(7, 16)]
// SAUT TRAMPOLINE (18/07, variantes aléatoires) : 3 poses par joueur, ligne 18 — [squash sur la
// toile, éjection bras au ciel, suspension à l'apex]. Positions explicites (hors pipeline
// runner2/runner3) : cols 0-2 J1, 3-5 J2, 6-8 J3. Synchro LAYOUT de gen-sprites.mjs.
export const TRAMP_FRAMES: readonly (readonly [SpriteRef, SpriteRef, SpriteRef])[] = [
  [ref(0, 18), ref(1, 18), ref(2, 18)],
  [ref(3, 18), ref(4, 18), ref(5, 18)],
  [ref(6, 18), ref(7, 18), ref(8, 18)],
]
export const RUNNER2_OFFSET_ROWS = 2
// Sprite du coureur 2 (vert) à partir d'un sprite J1 : même colonne, N lignes plus bas.
export const runner2 = (r: SpriteRef): SpriteRef => ({ sx: r.sx, sy: r.sy + RUNNER2_OFFSET_ROWS * CELL })
export const RUNNER3_OFFSET_ROWS = 6
// Sprite du coureur 3 (orange) à partir d'un sprite J1 : même colonne, 6 lignes plus bas (ligne 7).
export const runner3 = (r: SpriteRef): SpriteRef => ({ sx: r.sx, sy: r.sy + RUNNER3_OFFSET_ROWS * CELL })

// --- moine (normal = ligne 5) ---
export interface MonkFrames {
  readonly run: readonly [SpriteRef, SpriteRef]
  readonly climb: readonly [SpriteRef, SpriteRef]
  readonly trapped: SpriteRef
  readonly carried: SpriteRef
  readonly narguer: SpriteRef      // bras levés (Phase C) : jouée quand le moine est proche d'un coureur
}
export const MONK_FRAMES: MonkFrames = {
  run: [ref(0, 5), ref(1, 5)],
  climb: [ref(2, 5), ref(3, 5)],
  trapped: ref(4, 5),
  carried: ref(5, 5),
  narguer: ref(6, 5), // col 6 libre sur les lignes de moine ; monkSprite() la teinte par TYPE
}
// Variantes de couleur du moine par TYPE (Phase A). Le moine normal (violet) est sur la ligne 5
// (MONK_FRAMES) ; les 3 autres types sont les MÊMES frames recolorées sur des lignes libres,
// atteintes par un décalage de lignes depuis la ligne 5 (4/6/8 libres ; 7=J3, 9=mondes).
// DOIT rester synchro avec MONK_OFFSET_ROWS de scripts/gen-sprites.mjs (mêmes valeurs).
export const MONK_KIND_OFFSET_ROWS: Record<MonkKind, number> = {
  normal: 0,   // ligne 5 (violet, historique)
  sauteur: -1, // ligne 4 (vert)
  voleur: 1,   // ligne 6 (ambre/or)
  boss: 3,     // ligne 8 (rouge sombre)
  batisseur: 0, // mêmes frames que le normal : la teinte OCRE est appliquée au RENDU (drawTinted, 17/07) — aucune ligne de feuille consommée
}
// Sprite moine coloré selon le type : même colonne/frame, N lignes plus bas (ou plus haut).
export const monkSprite = (r: SpriteRef, kind: MonkKind): SpriteRef =>
  ({ sx: r.sx, sy: r.sy + MONK_KIND_OFFSET_ROWS[kind] * CELL })

// Version PIERRE d'un sprite (statue du gong d'or, 17/07) : désaturation par luminance + léger
// froid, calculée UNE fois par case de feuille et mise en cache. getImageData plutôt que
// ctx.filter : le rendu est identique sur tous les navigateurs (Safari inclus).
const greyCache = new Map<string, HTMLCanvasElement>()
export function greySprite(sheet: HTMLImageElement, r: SpriteRef): HTMLCanvasElement {
  const key = `${r.sx},${r.sy}`
  const hit = greyCache.get(key)
  if (hit) return hit
  const cv = document.createElement('canvas')
  cv.width = CELL
  cv.height = CELL
  const g = cv.getContext('2d')!
  g.drawImage(sheet, r.sx, r.sy, CELL, CELL, 0, 0, CELL, CELL)
  const img = g.getImageData(0, 0, CELL, CELL)
  const d = img.data
  for (let i = 0; i < d.length; i += 4) {
    const l = (0.3 * d[i]! + 0.55 * d[i + 1]! + 0.15 * d[i + 2]!) * 0.88
    d[i] = l
    d[i + 1] = l
    d[i + 2] = Math.min(255, l * 1.12) // pierre légèrement froide
  }
  g.putImageData(img, 0, 0)
  greyCache.set(key, cv)
  return cv
}

// Version de la feuille : à bumper quand le PNG change (ici : Lot C — 5 tuiles ligne 2 cols 9-13
// (tapis vertical, bombes rouges, trampolines directionnels) — ET les 15 décorations ligne 10).
const SHEET_VERSION = 'bulles-pop-1' // 22/07 : source de bulles raccordée à l'eau cartoon pop ; avant : pharaon-1
export function loadSheet(): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image()
    img.onload = () => res(img)
    img.onerror = rej
    img.src = `/assets/spritesheet.png?v=${SHEET_VERSION}`
  })
}

// --- feuille TUILES HD (world-tiles.png, refonte graphique 20/07, terminal DÉCOR) : les tuiles de
// TERRAIN en source 48 px, blittées 1:1 (net) au lieu d'upscaler la source 16 de spritesheet. MÊME
// grille que spritesheet, cellules x3 : la cellule d'un SpriteRef (coords 16) s'y trouve à (sx*3, sy*3)
// -> blitWorld() (render.ts) fait la conversion, donc AUCUNE ref de ce fichier ne change (éditeur et
// miniatures continuent de lire spritesheet en 16). Séparée du pipeline PERSO (spritesheet reste 16). ---
export const HD_TILE = 48 // côté d'une cellule HD dans world-tiles.png
// Tuiles HD par BIOME dans world-tiles.png (hors grille socle 0-19). Chaque biome occupe 3 lignes :
// brickRow (6 variantes sur une GAMME D'USURE, col = niveau) / concreteRow / decoRow (3 overlays de
// défaut transparents : fissure/cassée/gravats). render.ts choisit l'usure par bruit cohérent et pose
// un défaut rarement (hash apériodique). L'ORDRE DOIT matcher HD_BIOME_ORDER de gen-sprites.mjs.
const HD_BIOME_ORDER: readonly WorldKey[] = ['classique', 'grotte', 'prairie', 'desert', 'neige', 'usine', 'abysses', 'manoir']
export interface HdBiome { readonly brickRow: number; readonly concreteRow: number; readonly decoRow: number; readonly variants: number; readonly decos: number }
export const HD_BIOMES: Record<WorldKey, HdBiome> = Object.fromEntries(
  HD_BIOME_ORDER.map((k, i) => [k, { brickRow: 20 + i * 3, concreteRow: 21 + i * 3, decoRow: 22 + i * 3, variants: 6, decos: 3 }]),
) as Record<WorldKey, HdBiome>
export const HD_VARIANT_ROWS = HD_BIOME_ORDER.length * 3 // 24 : 8 biomes x 3 lignes (doit suivre gen-sprites HD_ROWS)
export const WORLD_SHEET_W = SHEET_W * 3 // 768
export const TRESOR_SHEET_ROWS = 2 // lignes 44-45 : les 20 trésors-décos (codes 32+, doit suivre gen-sprites TRESOR_ROWS)
export const WORLD_SHEET_H = SHEET_H * 3 + (HD_VARIANT_ROWS + TRESOR_SHEET_ROWS) * HD_TILE // 2208
const WORLD_SHEET_VERSION = 'bulles-pop-2' // bump à CHAQUE régénération de world-tiles.png (22/07 : pierre de pyramide pharaon-2 + source de bulles pop — corps d'eau pop, anneaux blancs)
let worldTilesImg: HTMLImageElement | null = null
/** La feuille tuiles HD une fois chargée (null avant loadWorldTilesSheet -> blitWorld retombe sur spritesheet 16). */
export const worldTilesSheet = (): HTMLImageElement | null => worldTilesImg
export function loadWorldTilesSheet(): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image()
    img.onload = () => { worldTilesImg = img; res(img) }
    img.onerror = rej
    img.src = `/assets/world-tiles.png?v=${WORLD_SHEET_VERSION}`
  })
}

// --- feuille GRANDS DÉCORS (decors.png, 18/07 « fais mieux ») : les 6 ensembles multi-cases
// en pixel art riche, 3 frames chacun. Géométrie dérivée de BIG_DECOS + BIG_DECO_ORDER
// (bandes empilées, frames côte à côte) — la même formule que gen-sprites.mjs, verrouillée
// par tests/decors-sheet.test.ts contre le PNG réellement encodé.
export const DECOR_SHEET_W = Math.max(...BIG_DECO_ORDER.map((k) => BIG_DECOS[k].w)) * CELL * BIG_FRAMES
export const DECOR_SHEET_H = BIG_DECO_ORDER.reduce((a, k) => a + BIG_DECOS[k].h * CELL, 0)
const BIG_Y0 = new Map<BigDecoKind, number>()
{
  let y = 0
  for (const k of BIG_DECO_ORDER) { BIG_Y0.set(k, y); y += BIG_DECOS[k].h * CELL }
}
/** Origine (px feuille) de la frame d'un grand décor ; sw/sh = w*CELL / h*CELL de sa meta. */
export const bigDecoSrc = (kind: BigDecoKind, frame: number): SpriteRef =>
  ({ sx: frame * BIG_DECOS[kind].w * CELL, sy: BIG_Y0.get(kind)! })

// --- feuille COUREURS CARTOON (runners.png, chantier skins 19/07) ---
// Géométrie : shared/skins.ts (cellules 32×32, art 24×28 à (4,4) → même boîte écran TS×TS
// que l'ancienne ligne coureur ; rangée = skin×teinte pré-cuite, plus AUCUN filtre au runtime).
// RUNNER_POSE : mêmes clés que RUNNER_FRAMES, mais en INDICES de colonnes de runners.png
// (ordre verrouillé sur POSES de shared/skins.ts par tests/skins-sheet.test.ts).
export const RUNNER_POSE = {
  idle: 0, run: [1, 2], climb: [3, 4], rope: [5, 6], fall: 7, dig: 8,
  crouch: 9, dash: [10, 11], brake: 12, pickUp: 13, pickHit: 14,
} as const
export const TRAMP_POSE = [15, 16, 17] as const
const RUNNERS_SHEET_VERSION = 'baroudeur-1' // 19/07 : Le Baroudeur seul héros (roster réduit) ; avant : cartoon-2 (10 skins)
let runnersImg: HTMLImageElement | null = null
/** La feuille coureurs une fois chargée (null avant loadRunnersSheet — les rendus skippent). */
export const runnersSheet = (): HTMLImageElement | null => runnersImg
export function loadRunnersSheet(): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image()
    img.onload = () => { runnersImg = img; res(img) }
    img.onerror = rej
    img.src = `/assets/runners.png?v=${RUNNERS_SHEET_VERSION}`
  })
}

// --- feuille COMPAGNONS HD (pets.png, 21/07 soir « beaucoup mieux ») : 10 familiers x 4 frames
// 48 px (colonne = frame : 0-1 déplacement, 2-3 oisiveté ; ligne = pet). Affichée RÉDUITE par
// espèce (client/pet.ts PET_SCALE) : plus petits qu'une case, bien plus détaillés. Synchro
// PET_ORDER de gen-sprites.mjs.
export const PET_HD_ROWS: Readonly<Record<string, number>> = {
  'pet-chat': 0, 'pet-canari': 1, 'pet-loup': 2, 'pet-perroquet': 3, 'pet-araignee': 4,
  'pet-serpent': 5, 'pet-dragonnet': 6, 'pet-chouette': 7, 'pet-renard': 8, 'pet-follet': 9,
}
// ⚠️ À BUMPER À CHAQUE RETOUCHE DE pets.png : la feuille est demandée avec ce jeton en query, et
// sans changement de jeton le cache HTTP du téléphone ressert l'ancienne image. On vérifierait
// alors la nouvelle sur un profil neuf, on livrerait, et la famille garderait l'ancienne.
// pets-hd-2 (26/07) : passe de relief + serpent lové et chat assis refaits + chaussettes du renard.
const PETS_SHEET_VERSION = 'pets-hd-2'
let petsImg: HTMLImageElement | null = null
/** La feuille compagnons une fois chargée (null avant loadPetsSheet — pet.ts retombe sur le 16 px). */
export const petsSheet = (): HTMLImageElement | null => petsImg
export function loadPetsSheet(): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image()
    img.onload = () => { petsImg = img; res(img) }
    img.onerror = rej
    img.src = `/assets/pets.png?v=${PETS_SHEET_VERSION}`
  })
}

const DECOR_SHEET_VERSION = 'pixel-riche-1' // à bumper à chaque retouche de decors.png
let decorImg: HTMLImageElement | null = null
/** La feuille décors une fois chargée (null avant loadDecorSheet — les rendus skippent). */
export const decorSheet = (): HTMLImageElement | null => decorImg
export function loadDecorSheet(): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image()
    img.onload = () => { decorImg = img; res(img) }
    img.onerror = rej
    img.src = `/assets/decors.png?v=${DECOR_SHEET_VERSION}`
  })
}
